(() => {
  const DEFAULT_API_URL = 'https://business-hub.vyte.sh/api/storefront-enquiry';
  // Hard cap for UI feedback — email may still finish later on the server.
  const SUCCESS_CAP_MS = 1000;
  // Safety only for hung connections when optimistic UI already finished.
  const REQUEST_TIMEOUT_MS = 20000;
  const DEFAULT_SENDING_LABEL = 'Sending…';
  const DEFAULT_SUCCESS_LABEL =
    "Thanks — we've received your enquiry and will be in touch shortly.";

  function resolveApiUrl() {
    const configured = window.TPU_STOREFRONT_ENQUIRY_API_URL;
    if (typeof configured === 'string' && configured.trim()) {
      return configured.trim().replace(/\/+$/, '');
    }
    return DEFAULT_API_URL;
  }

  function shopDomain() {
    const shop = window.Shopify && window.Shopify.shop;
    if (typeof shop === 'string' && shop.trim()) {
      return shop.trim().toLowerCase();
    }
    return '';
  }

  function fieldValue(form, name) {
    if (!form) return '';
    const el = form.querySelector(`[name="${name}"]`);
    if (!el) return '';
    if (el.type === 'checkbox') return el.checked ? 'Yes' : 'No';
    if (el.type === 'radio') {
      const checked = form.querySelector(`[name="${name}"]:checked`);
      return checked ? checked.value.trim() : '';
    }
    return String(el.value || '').trim();
  }

  function customerMessage(value, kind) {
    const text = String(value || '').trim();
    if (!text || /translation missing/i.test(text)) {
      return kind === 'sending' ? DEFAULT_SENDING_LABEL : DEFAULT_SUCCESS_LABEL;
    }
    return text;
  }

  function setSubmitLoading(button, loading) {
    if (!(button instanceof HTMLElement)) return;

    if (loading) {
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel = button.textContent || '';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = customerMessage(
        button.getAttribute('data-sending'),
        'sending',
      );
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (button.dataset.originalLabel) {
      button.textContent = button.dataset.originalLabel;
    }
  }

  async function submitEnquiry(payload) {
    const body = {
      shopDomain: shopDomain(),
      sourcePageUrl: window.location.href,
      ...payload,
    };

    const controller = new AbortController();
    const timeout = window.setTimeout(
      () => controller.abort(),
      REQUEST_TIMEOUT_MS,
    );

    let response;
    try {
      response = await fetch(resolveApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (error) {
      if (error && error.name === 'AbortError') {
        const timeoutError = new Error(
          'The request took too long. Please check your connection and try again.',
        );
        timeoutError.status = 408;
        throw timeoutError;
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.error || 'Unable to submit enquiry.');
      err.status = response.status;
      err.fieldErrors = data.fieldErrors || null;
      throw err;
    }
    return data;
  }

  /**
   * Always resolves for storefront UX. Email may finish later on the server.
   * Network / API failures are logged only — customers always see success.
   */
  function submitEnquiryOptimistic(payload, { maxWaitMs = SUCCESS_CAP_MS } = {}) {
    return new Promise((resolve) => {
      let settled = false;
      const request = submitEnquiry(payload);

      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve({ accepted: true, optimistic: true });
      }, maxWaitMs);

      request
        .then((data) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve({ accepted: true, optimistic: false, data });
        })
        .catch((err) => {
          console.warn('[TPU enquiry]', err);
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve({ accepted: true, optimistic: true, error: err });
        });
    });
  }

  function runEnquirySubmit({
    payload,
    button,
    successEl,
    sendingLabel,
    successLabel,
    onSuccess,
  } = {}) {
    const sending = customerMessage(
      sendingLabel ||
        (button && button.getAttribute('data-sending')) ||
        (successEl && successEl.getAttribute('data-sending')),
      'sending',
    );
    const success = customerMessage(
      successLabel || (successEl && successEl.textContent),
      'success',
    );

    const finishOk = () => {
      setSubmitLoading(button, false);
      if (window.ModalToast) {
        window.ModalToast.show(success);
      }
      if (typeof onSuccess === 'function') onSuccess();
    };

    setSubmitLoading(button, true);
    if (window.ModalToast) {
      window.ModalToast.show(sending, { sticky: true });
    }

    if (!payload) {
      console.warn('[TPU enquiry] missing payload');
      finishOk();
      return Promise.resolve({ accepted: true, optimistic: true });
    }

    return submitEnquiryOptimistic(payload).then(() => {
      finishOk();
    });
  }

  window.TpuStorefrontEnquiry = {
    fieldValue,
    shopDomain,
    submitEnquiry,
    submitEnquiryOptimistic,
    runEnquirySubmit,
    resolveApiUrl,
    setSubmitLoading,
    customerMessage,
    DEFAULT_SENDING_LABEL,
    DEFAULT_SUCCESS_LABEL,
    SUCCESS_CAP_MS,
  };
})();
